import React, { useState } from 'react'
import { Button } from 'primereact/button'

import { LogsTable, LOGS_NO_OF_ITEMS } from '../../../lib/logs-table'
import { WidgetLayout } from '../../../lib/widget-layout'
import { isMoreItemsToLoad } from '../../../lib/is-more-items-to-load'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../../../common/constants/ChargePointConstants'
import { useLogGetByLatestChargePointExcludingOcppQuery } from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import { CapiLogFilters, CapiLogsFilterDialog } from '../dialogs/capi-logs-filter-dialog'

export type ChargePointLogsWidgetProps = {
    chargePointId: string
}

export function ChargePointLogsWidget({ chargePointId }: ChargePointLogsWidgetProps): React.ReactElement {
    const text = useText()
    const [filters, setFilters] = useState<CapiLogFilters>({
        logLevel: { debug: false, info: false, warn: false, error: false },
        origin: {
            lb: true,
            ocpp: false,
            capi: true,
            measure: false,
            batch: false,
            device: true,
            web: false,
        },
    })
    const [noOfItems, setNoOfItems] = useState(LOGS_NO_OF_ITEMS)

    const {
        data: logs = [],
        isLoading,
        isFetching,
    } = useLogGetByLatestChargePointExcludingOcppQuery(
        {
            chargePointId,
            noOfItems,
            logFilter: filters,
        },
        { pollingInterval: CHARGE_POINT_REFRESH_TIMEOUT }
    )

    const [isFilterDialogVisible, setIsFilterDialogVisible] = useState(false)

    return (
        <WidgetLayout
            title={text('lblLogsWidget')}
            loadingStatus={isLoading || isFetching}
            headerButtons={
                <Button
                    type="button"
                    icon="pi pi-filter"
                    aria-label={text('btnFilterLogs')}
                    tooltip={text('btnFilterLogs')}
                    text
                    rounded
                    onClick={() => setIsFilterDialogVisible(!isFilterDialogVisible)}
                />
            }
            data-testid="logs"
        >
            <CapiLogsFilterDialog
                visible={isFilterDialogVisible}
                onHide={() => setIsFilterDialogVisible(false)}
                onApply={setFilters}
                filters={filters}
            />
            <LogsTable
                columns={[
                    {
                        label: text('lblTimestamp'),
                        accessor: 'timestamp',
                        width: 100,
                    },
                    {
                        label: text('lblLogLevel'),
                        accessor: 'logLevel',
                        width: 100,
                    },
                    {
                        label: text('lblOrigin'),
                        accessor: 'originShort',
                        width: 100,
                    },
                    {
                        label: text('lblConnectorShort'),
                        accessor: 'connectorId',
                        width: 50,
                    },
                    { label: text('lblMessage'), accessor: 'message' },
                ]}
                logs={logs}
                actions={
                    isMoreItemsToLoad(noOfItems, logs.length)
                        ? [
                              {
                                  title: text('btnLoadMore'),
                                  onClick: () => {
                                      setNoOfItems((current) => current + logs.length)
                                  },
                              },
                          ]
                        : []
                }
                loading={isLoading || isFetching}
            />
        </WidgetLayout>
    )
}
